import { Actions } from 'vuex-smart-module';
import { ResponseStatuses } from '@/enums';
import { AxiosResponse } from 'axios';
import { Filters, FiltersPagination, RequestPayload } from '@/types';
import SoftwareLicensesAddedModal
  from '@/modules/software-licenses/components/SoftwareLicensesAddedModal.vue';
import { SoftwareLicenses } from '@/modules/software-licenses/types';
import { SoftwareLicensesService } from '../services';
import SoftwareLicensesMutations from './mutations';
import SoftwareLicensesGetters from './getters';
import SoftwareLicensesState from './state';

const softwareLicensesService = new SoftwareLicensesService();

export default class SoftwareLicensesActions extends Actions<
  SoftwareLicensesState,
  SoftwareLicensesGetters,
  SoftwareLicensesMutations,
  SoftwareLicensesActions
> {
  async get_software_licenses() {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING_SOFTWARE_LICENSES(true);
      const { data, meta } = await softwareLicensesService.get_software_licenses(payload);

      // временно отключено, возможно в будущем понадобится
      // this.mutations.SET_IS_SOFTWARE_ORDER_ALLOWED(data.isSoftwareOrderAllowed);
      // this.mutations.SET_IS_MODAL_SOFTWARE_ORDER_ALLOWED(!data.isSoftwareOrderAllowed);

      if (data) {
        this.mutations.SET_SOFTWARE_LICENSES(data.items);
        this.mutations.SET_CITIES(data.cities);
        this.mutations.SET_STATUSES(data.statuses);
        this.mutations.SET_CATEGORIES(data.categories);
        this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
        this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
        this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
        this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
        this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_SOFTWARE_LICENSES(false);
    }
  }

  async get_not_active_software_licenses() {
    this.mutations.TOGGLE_NOT_ACTIVE_SOFTWARE_LICENSES_LOADING(true);

    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.not_active_software_licenses_table_options;

      const payload: RequestPayload<SoftwareLicenses.NotActiveSoftwareLicensesRequestFilters> = {
        filters: this.state.not_active_software_licenses_filters,
        filters_exist: this.state.not_active_software_licenses_statuses.length > 0,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      const { data, meta } = await softwareLicensesService.get_not_active_software_licenses(payload);

      if (data) {
        this.mutations.SET_NOT_ACTIVE_SOFTWARE_LICENSES(data.items);

        this.mutations.UPD_NOT_ACTIVE_SOFTWARE_LICENSES_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
        this.mutations.UPD_NOT_ACTIVE_SOFTWARE_LICENSES_TABLE_OPTIONS({ key: 'page', value: meta.current_page });

        this.mutations.UPD_NOT_ACTIVE_SOFTWARE_LICENSES_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
        this.mutations.UPD_NOT_ACTIVE_SOFTWARE_LICENSES_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
        this.mutations.UPD_NOT_ACTIVE_SOFTWARE_LICENSES_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
        this.mutations.UPD_NOT_ACTIVE_SOFTWARE_LICENSES_PAGINATION_FIELD({ key: 'limit', value: meta.limit });

        if (this.state.not_active_software_licenses_statuses.length < 1 && data.filters.statuses.length > 0) {
          this.mutations.SET_NOT_ACTIVE_SOFTWARE_LICENSES_STATUSES(data.filters.statuses);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_NOT_ACTIVE_SOFTWARE_LICENSES_LOADING(false);
    }
  }

  async get_software_license_by_id(id: string | number) {
    try {
      const { data, code = ResponseStatuses.Fail.FORBIDDEN } = await softwareLicensesService.get_software_license_by_id(id);

      this.mutations.SET_CURRENT_SOFTWARE_LICENSE(data);
      return code;
    } catch (error) {
      console.error(error);
      return (error as AxiosResponse).data.code;
    }
  }

  async get_software_licenses_history() {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options_software_licenses_history;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.software_licenses_history_filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING_SOFTWARE_LICENSES(true);
      const { data, meta } = await softwareLicensesService.get_software_licenses_history(payload);

      this.mutations.SET_SOFTWARE_LICENSES_HISTORY(data.items);
      this.mutations.SET_HISTORY_STATUSES(data.statuses);
      this.mutations.UPD_TABLE_OPTIONS_HISTORY({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_PAGINATION_FIELD_HISTORY({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD_HISTORY({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD_HISTORY({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD_HISTORY({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_SOFTWARE_LICENSES(false);
    }
  }

  async get_software_cart_history(id: string | number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: {},
        pagination: {
          limit: itemsPerPage,
          page,
          sort: 'order_date',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_LOADING_SOFTWARE_LICENSES(true);
      const { data, meta } = await softwareLicensesService.get_software_cart_history(payload, id);

      this.mutations.SET_SOFTWARE_CART_HISTORY(data.items);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.log(error);
    } finally {
      this.mutations.TOGGLE_LOADING_SOFTWARE_LICENSES(false);
    }
  }

  async licenses_in_archive(licenseIds: (string | number | undefined)[]) {
    try {
      await softwareLicensesService.licenses_in_archive(licenseIds);
    } catch (error) {
      console.error(error);
    }
  }
}
